import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDataModels } from "../services/dataModels/dataModelsApi";
import { listWorkspaceLinkedDataSources } from "../services/workspaces/workspaceLinkedDataSourcesApi";
import { queryKeys } from "../services/queryKeys";
import { resolveConnectorIconUrl } from "../utils/connectorIcons";
import type { DataSource, ModelOption } from "../components/organisms/StoryBuilder/StoryBuilder.types";

export interface UseWorkspacePickerDataResult {
  availableSources: DataSource[];
  modelOptions: ModelOption[];
  isSourcesLoading: boolean;
}

export interface UseWorkspacePickerDataOptions {
  /**
   * Gate for callers that stay mounted while hidden (e.g. the always-mounted
   * Create Data Story modal), so both lists load only once the picker is shown.
   */
  enabled?: boolean;
}

/**
 * Fetches workspace data-source connectors and data-model list for the
 * story-builder source picker.
 *
 * DataSource.id is connector `connection_id` (not workspace_connectors.id),
 * because story plan `data_sources` stores/expects connection IDs.
 */
export function useWorkspacePickerData(
  workspaceId: string,
  { enabled = true }: UseWorkspacePickerDataOptions = {},
): UseWorkspacePickerDataResult {
  const shouldFetch = enabled && Boolean(workspaceId);

  const { data: dataSourcesData, isLoading: isSourcesLoading } = useQuery({
    queryKey: queryKeys.workspaces.linkedDataSources(workspaceId),
    queryFn: () => listWorkspaceLinkedDataSources(workspaceId),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const { data: dataModelsData, isLoading: isModelsLoading } = useQuery({
    queryKey: queryKeys.dataModels.list(workspaceId),
    queryFn: () => fetchDataModels({ limit: 100 }, workspaceId),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const availableSources = useMemo<DataSource[]>(
    () =>
      (dataSourcesData?.items ?? []).map((s) => ({
        id: s.connectionId,
        name: s.name,
        logoUrl: resolveConnectorIconUrl({
          catalogKey: s.catalogKey,
          serverIconUrl: s.iconUrl,
        }),
      })),
    [dataSourcesData],
  );

  const modelOptions = useMemo<ModelOption[]>(
    () => (dataModelsData?.items ?? []).map((m) => ({ id: m.id, label: m.name })),
    [dataModelsData],
  );

  return {
    availableSources,
    modelOptions,
    isSourcesLoading: isSourcesLoading || isModelsLoading,
  };
}
